import { useState } from "react";
import {
  Alert,
  AlertColor,
  Snackbar,
} from "@mui/material";
import RateReviewIcon from '@mui/icons-material/RateReview';
import { CodeIcon, DeleteIcon, DeletePrompt, DeletePromptNo, DeletePromptYes, Version, VersionContainer, VersionRight, VersionTitle } from "../styles";
import { LatestBadge, MetadataItem, ReviewInput, TypeBadge } from "./styles";
import {  } from "./styles";
import ReusableModal from "../../../../Components/Modal";
import Status from "../../../../Components/Status";
import { ReviewButton, ReviewLabel, ReviewModalContainer, ReviewSection, ReviewSelect } from "../../styles";

interface RenderVersionProps {
  extensionID: string;
  version: any;
  isLatest: boolean;
  onRefresh?: () => void;
}

const typeConfig = {
  service: { label: "Service", color: "#3b82f6" },
  worker: { label: "Worker", color: "#8b5cf6" },
  ui: { label: "UI", color: "#ec4899" },
};

const extensionStatusOptions = [
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Deprecated", value: "deprecated" },
  { label: "Rejected", value: "rejected" },
];

export default function RenderVersion({ extensionID, version, isLatest, onRefresh }: RenderVersionProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewStatus, setReviewStatus] = useState<string>(version.status || "pending");
  const [reviewMessage, setReviewMessage] = useState<string>("");

  const [showStatus, setShowStatus] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [statusSeverity, setStatusSeverity] =
    useState<AlertColor>("error");
  const handleCloseStatus = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setShowStatus(false);
  };

  const deleteExtensionVersion = async (
    _extensionID: string,
    _version: string
  ) => {
    console.log(
      `Removing following version: ${_extensionID}:${_version}`
    );
    fetch("/api/v1/extension/version/remove", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        extension: _extensionID,
        version: _version,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        console.log(data);
        if (data.success === true) {
          setStatusMsg(
            `Version ${_version} was deleted!`
          );
          setStatusSeverity("success");
          setShowStatus(true);
          onRefresh?.();
          setTimeout(() => {
            setShowStatus(false);
          }, 5000);
          return data.success;
        } else throw new Error(data.reason);
      });
  };

  const changeStatusExtensionVersion = async (
    _extensionID: string,
    _version: string,
    _status: string
  ) => {
    console.log(
      `Changing status of version ${_extensionID}:${_version} to ${_status}`
    );
    fetch("/api/v1/extension/version/changeStatus", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        extension: _extensionID,
        version: _version,
        status: _status,
        reviewMessage,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        console.log(data);
        if (data.success === true) {
          setStatusMsg(
            `Status of version ${_version} was changed to ${_status}!`
          );
          setStatusSeverity("success");
          setShowStatus(true);
          onRefresh?.();
          setTimeout(() => {
            setShowStatus(false);
          }, 5000);
          return data.success;
        } else throw new Error(data.reason);
      });
  };

  return (
    <>
      <VersionContainer item xs={12}>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", flex: 1, paddingLeft: "1rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
            <VersionTitle>
              v{version.version}
            </VersionTitle>
            {isLatest && <LatestBadge>Latest</LatestBadge>}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
            <Version style={{ margin: 0 }}>
              {version.description !== "" && version.description !== " "
                ? `Description: ${version.description}`
                : "No description"}
            </Version>
          </div>
        </div>

        <VersionRight>
          <Status status={version.status} />
          <CodeIcon
            onClick={() => setIsReviewModalOpen(true)}
          >
            <RateReviewIcon />
            Review
          </CodeIcon>

          {!isDeleting ? (
            <DeleteIcon onClick={() => setIsDeleting(true)} />
          ) : (
            <DeletePrompt show>
              Delete?{" "}
              <DeletePromptYes
                onClick={() => {
                  setIsDeleting(false);
                  deleteExtensionVersion(
                    extensionID!,
                    version.version
                  );
                }}
              />{" "}
              <DeletePromptNo onClick={() => setIsDeleting(false)} />
            </DeletePrompt>
          )}
        </VersionRight>
      </VersionContainer>

      <ReusableModal open={isReviewModalOpen} onClose={() => setIsReviewModalOpen(false)}>
        <ReviewModalContainer>
          <h2>Review Version {version.version}</h2>

          <ReviewSection>
            <ReviewLabel>Type</ReviewLabel>
            <MetadataItem>
              <TypeBadge type={version.type}>
                {typeConfig[version.type as keyof typeof typeConfig]?.label}
              </TypeBadge>
            </MetadataItem>
          </ReviewSection>

          <ReviewSection>
            <ReviewLabel>Status</ReviewLabel>
            <ReviewSelect
              value={reviewStatus}
              onChange={(e) => setReviewStatus(e.target.value)}
            >
              {extensionStatusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </ReviewSelect>
          </ReviewSection>

          <ReviewSection>
            <ReviewLabel>Review Message</ReviewLabel>
            <ReviewInput
              placeholder="Add a review message..."
              value={reviewMessage}
              onChange={(e) => setReviewMessage(e.target.value)}
            />
          </ReviewSection>

          <ReviewSection className="buttons">
            <ReviewButton
              onClick={() => setIsReviewModalOpen(false)}
              cancel
            >
              Cancel
            </ReviewButton>
            <ReviewButton
              onClick={() => {
                changeStatusExtensionVersion(
                  extensionID!,
                  version.version,
                  reviewStatus
                );
                setIsReviewModalOpen(false);
                setReviewMessage("");
              }}
            >
              Submit Review
            </ReviewButton>
          </ReviewSection>
        </ReviewModalContainer>
      </ReusableModal>

      <Snackbar
        open={showStatus}
        autoHideDuration={6000}
        onClose={handleCloseStatus}
      >
        <Alert
          onClose={handleCloseStatus}
          severity={statusSeverity}
          sx={{ width: "100%" }}
        >
          {statusMsg}
        </Alert>
      </Snackbar>
    </>
  )
}